import { useState, useEffect, useRef, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Search, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useTermSearchSuggestions } from "@/hooks/useTerms";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { wordFirstLetter } from "@/lib/normalizeText";

interface SearchBarProps {
  variant?: "hero" | "compact";
  className?: string;
}

interface Suggestion {
  id: string;
  word: string;
}

type DropdownPosition = {
  top: number;
  left: number;
  width: number;
};

export function SearchBar({ variant = "compact", className }: SearchBarProps) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const { data: suggestRows } = useTermSearchSuggestions(debouncedQuery);

  const suggestions = useMemo((): Suggestion[] => {
    if (debouncedQuery.length < 2 || !suggestRows?.length) {
      return [];
    }
    return suggestRows.map(term => ({ id: term.id, word: term.word }));
  }, [debouncedQuery, suggestRows]);

  const suggestionsOpen = showSuggestions && suggestions.length > 0;

  const updateDropdownPosition = () => {
    const el = anchorRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
    });
  };

  useLayoutEffect(() => {
    if (!suggestionsOpen) {
      setDropdownPos(null);
      return;
    }
    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);
    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [suggestionsOpen, suggestions.length, query, variant]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [debouncedQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        wrapperRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setShowSuggestions(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (searchQuery?: string) => {
    const q = searchQuery || query;
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setShowSuggestions(false);
      setQuery("");
    }
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    navigate(`/term/${suggestion.id}`);
    setShowSuggestions(false);
    setQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSuggestionClick(suggestions[selectedIndex]);
      } else {
        handleSearch();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const isHero = variant === "hero";

  const dropdownPanel =
    suggestionsOpen && dropdownPos ? (
      <div
        ref={dropdownRef}
        role="listbox"
        style={{
          position: "fixed",
          top: dropdownPos.top,
          left: dropdownPos.left,
          width: dropdownPos.width,
        }}
        className={cn(
          "z-[200] max-h-[min(18rem,50vh)] overflow-y-auto rounded-xl border border-sky-200 bg-sky-50 shadow-2xl ring-1 ring-primary/15",
          "text-slate-900"
        )}
      >
        {suggestions.map((suggestion, index) => (
          <button
            key={suggestion.id}
            type="button"
            role="option"
            aria-selected={index === selectedIndex}
            onClick={() => handleSuggestionClick(suggestion)}
            className={cn(
              "group flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left",
              index === selectedIndex
                ? "bg-primary/10 text-primary"
                : "text-slate-900 hover:bg-sky-100/80",
              index < suggestions.length - 1 && "border-b border-sky-100/90"
            )}
          >
            <div className="flex min-w-0 items-center gap-3">
              <div
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-semibold",
                  index === selectedIndex
                    ? "bg-primary/20 text-primary"
                    : "bg-sky-100 text-slate-700"
                )}
              >
                {wordFirstLetter(suggestion.word)}
              </div>
              <span
                className={cn(
                  "truncate font-medium",
                  index === selectedIndex ? "text-primary" : "text-slate-900"
                )}
              >
                {suggestion.word}
              </span>
            </div>
            <ArrowRight
              className={cn(
                "h-4 w-4 shrink-0 text-slate-400 transition-opacity group-hover:text-primary",
                index === selectedIndex
                  ? "text-primary opacity-100"
                  : "opacity-70 group-hover:opacity-100"
              )}
            />
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div ref={wrapperRef} className={cn("relative z-20 w-full", className)}>
      <div className="flex gap-2">
        <div ref={anchorRef} className="relative z-20 flex-1">
          <Search
            className={cn(
              "pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-slate-500",
              isHero ? "h-5 w-5" : "h-4 w-4"
            )}
          />
          <Input
            ref={inputRef}
            type="text"
            placeholder={t("search.placeholder")}
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setSelectedIndex(-1);
              setShowSuggestions(true);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(true)}
            className={cn(
              "relative z-20 border-0 bg-sky-50 pl-12 pr-4 text-slate-900 placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-primary/40",
              isHero
                ? "h-12 rounded-xl text-base shadow-lg"
                : "h-10 rounded-lg text-sm"
            )}
          />
        </div>
        {isHero && (
          <Button
            onClick={() => handleSearch()}
            className="bg-primary hover:bg-primary/90 btn-shimmer relative z-20 h-12 shrink-0 rounded-xl px-6 shadow-lg"
          >
            <Search className="h-5 w-5" />
          </Button>
        )}
      </div>

      {typeof document !== "undefined" &&
        createPortal(dropdownPanel, document.body)}
    </div>
  );
}
